/**
 * ============================================
 * LEVEL SETTINGS MODAL
 * ============================================
 *
 * Categorize levels into the five analytics groups:
 *   Single Player · Labz · UGC · 2D · PVP
 *
 * Categorization is a GLOBAL, S3-backed manifest (see LevelSettingsContext).
 * An admin edits behind an exclusive lock acquired on open; concurrent admins
 * and non-admins see a read-only view. Edits are staged in a local draft and
 * persisted with Save. Anything left unassigned defaults to UGC.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import {
  X, Search, Gamepad2, FlaskConical, Users, Square, Swords,
  Lock, Eye, Save, Check, Loader2, AlertTriangle,
} from 'lucide-react';
import {
  useLevelSettings, getDefaultCategory, type LevelCategory,
} from '@/context/LevelSettingsContext';

interface LevelSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  allLevels: string[];
  isDark: boolean;
}

type Category = Exclude<LevelCategory, null>;

const CATEGORY_CONFIG: Record<Category, { label: string; icon: React.ReactNode; color: string; bgColor: string }> = {
  singleplayer: { label: 'Single Player', icon: <Gamepad2 className="h-4 w-4" />,    color: 'text-emerald-400', bgColor: 'bg-emerald-500/20 border-emerald-500/50' },
  labz:         { label: 'Labz',          icon: <FlaskConical className="h-4 w-4" />, color: 'text-purple-400',  bgColor: 'bg-purple-500/20 border-purple-500/50' },
  ugc:          { label: 'UGC',           icon: <Users className="h-4 w-4" />,        color: 'text-blue-400',    bgColor: 'bg-blue-500/20 border-blue-500/50' },
  twod:         { label: '2D',            icon: <Square className="h-4 w-4" />,       color: 'text-amber-400',   bgColor: 'bg-amber-500/20 border-amber-500/50' },
  pvp:          { label: 'PVP',           icon: <Swords className="h-4 w-4" />,       color: 'text-red-400',     bgColor: 'bg-red-500/20 border-red-500/50' },
};

const CATEGORY_ORDER: Category[] = ['singleplayer', 'labz', 'ugc', 'twod', 'pvp'];

export function LevelSettingsModal({ isOpen, onClose, allLevels, isDark }: LevelSettingsModalProps) {
  const {
    levelSettings, getEffectiveCategory, isAdmin, lockMine, lock,
    acquiring, saving, error, legacyLocalSettings, manifestVersion,
    manifestUpdatedBy, saveManifest,
  } = useLevelSettings();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<Category | 'all'>('all');
  const [draft, setDraft] = useState<Record<string, LevelCategory>>({});
  const [justSaved, setJustSaved] = useState(false);
  const seededRef = useRef(false);

  const editable = isAdmin && lockMine;

  // Seed the draft once per edit session. First-time migration: if the manifest
  // is still empty but this browser has legacy localStorage assignments, seed
  // from those so the admin can review + Save them into the shared manifest.
  useEffect(() => {
    if (editable && !seededRef.current) {
      seededRef.current = true;
      const seedFromLegacy = manifestVersion === 0 && Object.keys(legacyLocalSettings).length > 0;
      setDraft({ ...(seedFromLegacy ? legacyLocalSettings : levelSettings) });
    }
    if (!isOpen) {
      seededRef.current = false;
      setDraft({});
      setJustSaved(false);
      setSearchQuery('');
      setFilterCategory('all');
    }
  }, [editable, isOpen, manifestVersion, levelSettings, legacyLocalSettings]);

  // Resolved category for a row (draft while editing, saved otherwise).
  const effectiveOf = (level: string): Category => {
    if (editable) {
      const d = draft[level];
      if (d) return d;
      return getDefaultCategory(level) ?? 'ugc';
    }
    return getEffectiveCategory(level);
  };

  const filteredLevels = useMemo(() => {
    return allLevels
      .filter(level => {
        const matchesSearch = level.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filterCategory === 'all' || effectiveOf(level) === filterCategory;
        return matchesSearch && matchesFilter;
      })
      .sort((a, b) => a.localeCompare(b));
    // effectiveOf depends on draft/levelSettings/editable; listed deps cover it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLevels, searchQuery, filterCategory, draft, levelSettings, editable]);

  const categoryCounts = useMemo(() => {
    const counts: Record<Category, number> = { singleplayer: 0, labz: 0, ugc: 0, twod: 0, pvp: 0 };
    allLevels.forEach(level => { counts[effectiveOf(level)]++; });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLevels, draft, levelSettings, editable]);

  const dirty = useMemo(() => {
    const keys = new Set([...Object.keys(draft), ...Object.keys(levelSettings)]);
    for (const k of keys) {
      if ((draft[k] ?? null) !== (levelSettings[k] ?? null)) return true;
    }
    return false;
  }, [draft, levelSettings]);

  if (!isOpen) return null;

  const setCat = (level: string, cat: Category) => {
    if (!editable) return;
    setJustSaved(false);
    // Always set the category explicitly. The 5 categories are exhaustive
    // (everything resolves to one), so there is no "unset" state to toggle back
    // to -- and a toggle-off would silently revert an explicit override to its
    // name-pattern default (e.g. un-pinning a level forced to UGC back to a
    // 'jungle'->Single Player match), which is a footgun.
    setDraft(prev => (prev[level] === cat ? prev : { ...prev, [level]: cat }));
  };

  const handleSave = async () => {
    const ok = await saveManifest(draft);
    if (ok) setJustSaved(true);
  };

  const bgColor = isDark ? 'bg-gray-900' : 'bg-white';
  const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';
  const textColor = isDark ? 'text-gray-100' : 'text-gray-900';
  const mutedText = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-300';

  // Status banner content
  let banner: { icon: React.ReactNode; text: string; cls: string } | null = null;
  if (acquiring) {
    banner = { icon: <Loader2 className="h-4 w-4 animate-spin" />, text: 'Acquiring edit lock…', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' };
  } else if (!isAdmin) {
    banner = { icon: <Eye className="h-4 w-4" />, text: 'Read-only — admin access is required to edit level categories.', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' };
  } else if (!lockMine && lock) {
    banner = { icon: <Lock className="h-4 w-4" />, text: `Editing in progress by ${lock.owner}. Try again once they finish.`, cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' };
  } else if (editable) {
    banner = { icon: <Check className="h-4 w-4" />, text: 'You hold the edit lock. Changes apply to all clients after Save.', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' };
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className={`relative w-full max-w-3xl max-h-[85vh] ${bgColor} rounded-xl shadow-2xl border ${borderColor} flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className={`p-4 border-b ${borderColor} flex items-center justify-between`}>
          <div>
            <h2 className={`text-lg font-semibold ${textColor}`}>Level Settings</h2>
            <p className={`text-sm ${mutedText}`}>
              Categorize levels for analytics grouping
              {manifestUpdatedBy ? ` · last edited by ${manifestUpdatedBy}` : ''}
            </p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg hover:bg-gray-700/50 transition-colors ${mutedText}`}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Status banner */}
        {banner && (
          <div className={`mx-4 mt-3 px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${banner.cls}`}>
            {banner.icon}<span>{banner.text}</span>
          </div>
        )}
        {error && (
          <div className="mx-4 mt-2 px-3 py-2 rounded-lg border text-sm flex items-center gap-2 bg-red-500/15 text-red-300 border-red-500/30">
            <AlertTriangle className="h-4 w-4" /><span>{error}</span>
          </div>
        )}

        {/* Category filter chips */}
        <div className={`p-4 border-b ${borderColor} flex gap-2 flex-wrap`}>
          <button
            onClick={() => setFilterCategory('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterCategory === 'all' ? 'bg-gray-600 text-white'
                : `${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} ${mutedText}`
            }`}
          >
            All ({allLevels.length})
          </button>
          {CATEGORY_ORDER.map(key => {
            const config = CATEGORY_CONFIG[key];
            return (
              <button
                key={key}
                onClick={() => setFilterCategory(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  filterCategory === key ? `${config.bgColor} ${config.color} border`
                    : `${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} ${mutedText}`
                }`}
              >
                {config.icon}
                {config.label} ({categoryCounts[key]})
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className={`p-4 border-b ${borderColor}`}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${mutedText}`} />
            <input
              type="text"
              placeholder="Search levels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2.5 rounded-lg border ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
            />
          </div>
        </div>

        {/* Level list */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {filteredLevels.length === 0 ? (
              <div className={`text-center py-8 ${mutedText}`}>No levels found matching your search</div>
            ) : (
              filteredLevels.map(level => {
                const cat = effectiveOf(level);
                const config = CATEGORY_CONFIG[cat];
                return (
                  <div key={level} className={`flex items-center justify-between p-3 rounded-lg border ${borderColor} ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={config.color}>{config.icon}</span>
                      <span className={`font-mono text-sm truncate ${textColor}`}>{level}</span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {CATEGORY_ORDER.map(c => {
                        const cc = CATEGORY_CONFIG[c];
                        const isSelected = cat === c;
                        return (
                          <button
                            key={c}
                            onClick={() => setCat(level, c)}
                            disabled={!editable}
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                              isSelected ? `${cc.bgColor} ${cc.color} border`
                                : `${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} ${mutedText}`
                            } ${!editable ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            {cc.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${borderColor} flex items-center justify-between gap-3`}>
          <div className="text-sm">
            {justSaved && !dirty && <span className="text-emerald-400 flex items-center gap-1.5"><Check className="h-4 w-4" /> Saved — visible to all clients on next refresh</span>}
            {editable && dirty && <span className={mutedText}>Unsaved changes</span>}
          </div>
          <div className="flex items-center gap-2">
            {editable && (
              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  saving || !dirty ? 'bg-emerald-600/40 text-white/70 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                }`}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
